<template>
  <div class="rf-segments rf-main-content-wrapper">
    <RfLimitWarning class="mb-4" />
    <div class="flex min-h-12 items-center justify-between gap-16">
      <div class="flex min-w-px flex-1 items-center gap-8">
        <h1 class="shrink-0 text-xl font-semibold not-italic">All segments</h1>
        <RfTagsGroup
          class="pt-2 pb-1"
          :filters="filters"
          @remove="(filterKey, key) => (removeFilter(filterKey, key), refetch())"
          @clear="((filters = {}), refetch())"
        />
      </div>
      <div class="flex grow-0 items-center gap-4">
        <RfSegmentCreateModal v-model="segmentCreateDialog" />
        <RfButtonInput
          v-model="searchInputs.inputs.value[SEGMENTS_SEARCH][appsStore.app.id]"
          text="Search"
          data-cy="segments--search"
          placeholder="Search"
          :icon="RfSearchIcon"
          @update:modelValue="refetch"
        />
        <RfButtonNew
          text="+ New Segment"
          type="main"
          size="large"
          data-cy="segments--create-segment"
          :disabled="userStore.isDisabledWithMessage"
          @click="showSegmentCreateDialog"
        />
      </div>
    </div>
    <RfTable
      :tableData="segmentsStore.table"
      :loading="segmentsStore.loading"
      :loadingMore="segmentsStore.loadingMore"
      class="mt-4"
      :rowSkeleton="RfTableRowSkeleton"
      type="segments"
      variant="outlined"
      @loadMore="getSegments(true)"
    >
      <template #pretable>
        <span class="mb-0 inline-flex items-stretch gap-4">
          <RfTableSelect
            :selected="selected"
            :tableData="segmentsStore.table"
            @delete="((deleteItem = valuesSelected), nextTick(() => deleteModal.show()))"
            @edit="
              ((editItem = selectedValue),
              nextTick(() =>
                renameModal.open({ name: editItem.name, description: editItem.description }),
              ))
            "
            @clone="((cloneItem = selectedValue), nextTick(() => cloneModal.show()))"
            @export="exportMultipleSegmentsToCSV([...selected.values()], date.preset?.value)"
            @status="
              status => (
                (statusUpdate = { status, segments: valuesSelected }),
                nextTick(() => statusModal.show())
              )
            "
            @selectAll="segmentsStore.segments.forEach(v => updateMap('set', v))"
            @deselectAll="segmentsStore.segments.forEach(v => updateMap('delete', v))"
            @selectByPreset="selectByPreset"
          />
        </span>
        <span class="my-2 inline-flex items-center gap-4">
          <RfDatePicker
            ref="datePicker"
            :include="['today', 'last_seven_days', 'this_week', 'this_month']"
            hideCustom
            @input="v => ((date = v), refetch())"
          />
        </span>
      </template>
      <template #thead>
        <RfTableHeader
          v-model:filters="filters"
          :sort="sort"
          @update:sort="v => ((sort = v), refetch())"
          @update:filters="refetch"
        />
      </template>
      <template #tbody>
        <RfTableRow
          v-for="segment in segmentsStore.segments"
          :key="segment.id"
          v-memo="[
            segment,
            selected.has(segment.id),
            isRemoving(segment.id),
            date.preset?.value,
            appsStore.app.timezone_offset,
            appsStore.app.custom_devices,
            userStore.isDisabledWithMessage,
          ]"
          :class="{ 'rf-row-removing': isRemoving(segment.id) }"
          :segment="segment"
          :timePeriod="date.preset?.value"
          :disabled="userStore.isDisabledWithMessage"
          :timezone="appsStore.app.timezone_offset"
          :selected="selected.has(segment.id)"
          :filters="buildFilters(segment)"
          :customDevices="appsStore.app.custom_devices"
          @select="updateMap('set', segment)"
          @deselect="updateMap('delete', segment)"
          @clone="((cloneItem = segment), nextTick(() => cloneModal.show()))"
          @status="
            status => (
              (statusUpdate = { status, segments: [segment] }),
              nextTick(() => statusModal.show())
            )
          "
          @edit="
            ((editItem = segment),
            nextTick(() =>
              renameModal.open({ name: editItem.name, description: editItem.description }),
            ))
          "
          @delete="((deleteItem = [segment]), nextTick(() => deleteModal.show()))"
        />
      </template>
    </RfTable>
    <RfBaseModal
      v-if="cloneItem"
      ref="cloneModal"
      width="580"
      message="Do you want to clone this segment?"
      secondaryText="Clone"
      @secondary="cloneSegment"
      @close="cloneItem = null"
    >
      <template #title>
        Are you sure you want to clone
        <span class="font-bold whitespace-pre"> {{ cloneItem.name }} </span> ?
      </template>
    </RfBaseModal>
    <RfEditModal
      v-if="editItem"
      ref="renameModal"
      :title="`Edit segment ${editItem.name}`"
      @confirm="v => updateSegment([{ ...v, id: editItem.id }])"
      @close="editItem = null"
    />
    <RfBaseModal
      v-if="deleteItem"
      ref="deleteModal"
      :message="`${deleteDialogText.body} This cannot be undone.`"
      width="580"
      errorText="Delete"
      @error="submitDelete"
      @close="deleteItem = null"
    >
      <template #title>
        Are you sure you want to delete
        <span class="font-bold whitespace-pre"> {{ deleteDialogText.title }} </span> ?
      </template>
    </RfBaseModal>
    <RfBaseModal
      v-if="statusUpdate.segments"
      ref="statusModal"
      :title="`${capitalize(statusChangeDialogText.title)} Segment`"
      width="580"
      :secondaryText="capitalize(statusChangeDialogText.title)"
      @secondary="updateSegmentStatus"
      @close="statusUpdate = { status: null, segments: null }"
    >
      <template #body>
        <span v-if="statusUpdate.segments.length > 1">
          Are you sure you want to {{ statusChangeDialogText.title }}
          <b>segments({{ statusUpdate.segments.length }})</b>?
          <b>These segments({{ statusUpdate.segments.length }})</b> will
          {{ statusChangeDialogText.body }}
        </span>
        <span v-else>
          Are you sure you want to {{ statusChangeDialogText.title }} this segment? The segment will
          {{ statusChangeDialogText.body }}
        </span>
      </template>
    </RfBaseModal>
  </div>
</template>

<script setup>
import RfTable from "@/blocks/RfTable/RfTable.vue";
import RfButtonNew from "@/components/buttons/RfButtonNew.vue";
import RfSearchIcon from "@/components/icons/RfSearchIcon.vue";
import RfDatePicker from "@/components/inputs/RfDatePicker.vue";
import RfTableHeader from "@/blocks/RfSegments/RfTableHeader.vue";
import RfTableRow from "@/blocks/RfSegments/RfTableRow.vue";
import RfButtonInput from "@/components/inputs/RfButtonInput.vue";
import RfSegmentCreateModal from "@/components/RfSegments/RfSegmentCreateModal.vue";
import { useUserSettings } from "@/utils/composables/useUserSettings";
import MetricsUtils from "@/utils/MetricsUtils";
import { nextTick, onMounted, ref, computed, useTemplateRef } from "vue";
import { useRouter } from "vue-router";
import { debounce } from "@/utils/debounce";
import { useSegmentsStore } from "@/pinia/segmentsStore";
import { useSegmentFilters } from "@/utils/composables/useSegmentFilters";
import RfTableRowSkeleton from "@/blocks/RfSegments/RfTableRowSkeleton.vue";
import { Statuses } from "@/utils/constants/SegmentsConstants";
import ApiSegments from "@/apis/ApiSegments";
import RfBaseModal from "@/components/modals/RfBaseModal.vue";
import RfTableSelect from "@/blocks/RfSegments/RfTableSelect.vue";
import RfEditModal from "@/components/modals/RfEditModal.vue";
import { useAbortable } from "@/utils/composables/useAbortable";
import { useTableFilter } from "@/utils/composables/useTableFilter";
import { useTableSelect } from "@/utils/composables/useTableSelect";
import { useTableSort } from "@/utils/composables/useTableSort";
import { useRowRemoval } from "@/utils/composables/useRowRemoval";
import StringUtils from "@/utils/StringUtils";
import { useToastsStore } from "@/pinia/toastsStore";
import { exportMultipleSegmentsToCSV } from "@/utils/CsvUtils";
import { useAppsStore } from "@/pinia/appsStore";
import { useUserStore } from "@/pinia/userStore";
import { SEGMENTS_SEARCH, useSearchInputs } from "@/utils/composables/useSearchInputs";
import RfTagsGroup from "@/blocks/RfTagsGroup.vue";
import RfLimitWarning from "@/components/RfCommonCards/RfLimitWarning.vue";

const segmentsStore = useSegmentsStore();
const appsStore = useAppsStore();
const userStore = useUserStore();
const toastsStore = useToastsStore();
const router = useRouter();
const { getUserSetting, setUserSettings } = useUserSettings();
const { buildFilters } = useSegmentFilters();

const searchInputs = useSearchInputs();
const date = ref({});
const segmentCreateDialog = ref(false);
const showSegmentCreateDialog = () => (segmentCreateDialog.value = true);

const { filters, removeFilter, filtersConverted } = useTableFilter();
const { selected, updateMap, resetSelected, selectedValue, valuesSelected } = useTableSelect();
const sort = useTableSort(["name", "created_at"], { type: "name", value: "asc" });
const { isRemoving, removeWithFade } = useRowRemoval();

const deleteItem = ref(null);
const editItem = ref(null);
const cloneItem = ref(null);
const statusUpdate = ref({ status: null, segments: null });

const datePicker = useTemplateRef("datePicker");
const cloneModal = useTemplateRef("cloneModal");
const renameModal = useTemplateRef("renameModal");
const deleteModal = useTemplateRef("deleteModal");
const statusModal = useTemplateRef("statusModal");

const capitalize = StringUtils.capitalize;

const deleteDialogText = computed(() => {
  const items = deleteItem.value;
  if (!items?.length) return { title: "", body: "" };
  if (items.length > 1)
    return {
      title: `${items.length} segments`,
      body: `Deleting this segments(${items.length}) removes all information about these segments(${items.length}).`,
    };
  return {
    title: items[0].name,
    body: "Deleting this segment removes all information about this segment.",
  };
});

const statusChangeDialogText = computed(() => {
  if (statusUpdate.value.status === Statuses.true.type)
    return { title: "start", body: "run immediately." };
  return { title: "stop", body: "stop running immediately." };
});

const fetchSegments = async (next = false) => {
  if (!date.value.preset) return;
  const statuses = filtersConverted.value.statuses?.string;
  searchInputs.write(
    SEGMENTS_SEARCH,
    appsStore.app.id,
    searchInputs.inputs.value[SEGMENTS_SEARCH][appsStore.app.id],
  );

  const result = await abortableFetch(signal =>
    segmentsStore.getSegmentsPaged(
      appsStore.app.id,
      {
        metric_periods: [
          {
            period: date.value.preset.value,
            ...(date.value.preset.value === "custom" && {
              date_range: [
                date.value.startDate.format("YYYY-MM-DD"),
                date.value.endDate.format("YYYY-MM-DD"),
              ],
            }),
          },
        ],
        ...(searchInputs.inputs.value[SEGMENTS_SEARCH][appsStore.app.id] && {
          name_search: searchInputs.inputs.value[SEGMENTS_SEARCH][appsStore.app.id],
        }),
        ...(statuses && { statuses }),
        ...(sort.value.string && { order: sort.value.string }),
      },
      next,
      { signal },
    ),
  );

  // Bail when aborted or upstream error — don't persist settings from a
  // request that never made it to the user.
  if (result === null) return;

  await setUserSettings({
    segmentsSort: sort.value.string,
    segmentsStatusFilter: statuses,
    ...(date.value.preset.value !== "custom" ? { segmentsDateRange: date.value.preset.value } : {}),
  });
};

const abortableFetch = useAbortable();
const getSegments = debounce(fetchSegments, 1000);

const refetch = () => {
  resetSelected();
  segmentsStore.resetPage();
  getSegments();
};

const editSegments = segments => {
  resetSelected();
  return segmentsStore.bulkUpdate(appsStore.app.id, segments);
};

const updateSegment = newSegments => {
  editSegments(newSegments)
    .then(() => toastsStore.create({ type: "success", body: "Segment updated" }))
    .catch(() => null);
};

const updateSegmentStatus = () => {
  const segments = Array.isArray(statusUpdate.value.segments)
    ? statusUpdate.value.segments
    : [statusUpdate.value.segments];
  const is_enabled = statusUpdate.value.status;
  const newSegments = segments.map(({ id }) => ({ id, is_enabled }));
  editSegments(newSegments)
    .then(() => {
      statusModal.value?.close();
      toastsStore.create({ type: "success", body: "Status updated" });
    })
    .catch(() => null);
};

const cloneSegment = () => {
  if (cloneItem.value.segment_type?.startsWith("system_")) {
    toastsStore.addToast({
      type: "error",
      message:
        "System segments cannot be cloned. Only one segment of this type is allowed per app.",
    });
    return;
  }

  const newSegment = {
    ...cloneItem.value,
    id: null,
    is_locked: false,
    name: `${cloneItem.value.name} copy`,
  };

  ApiSegments.createSegment(appsStore.app.id, newSegment)
    .then(({ id }) => router.push({ path: `/apps/${appsStore.app.id}/segments/${id}` }))
    .catch(error => {
      const errorMessage =
        error?.response?.data?.segment_type?.[0] || "Failed to clone segment. Please try again.";
      toastsStore.addToast({ type: "error", message: errorMessage });
    });
};

const submitDelete = async () => {
  const items = deleteItem.value;
  const body = `Segment${items.length > 1 ? "s" : ""} deleted`;

  try {
    await removeWithFade(
      items.map(s => s.id),
      async () => {
        await segmentsStore.bulkDelete(appsStore.app.id, items);
        resetSelected();
        await segmentsStore.reloadAllLoaded(() => fetchSegments());
      },
    );
    deleteModal.value?.close();
    toastsStore.create({ type: "success", body });
  } catch (e) {
    toastsStore.create({ type: "error", body: e.message });
  }
};

const selectByPreset = preset => {
  resetSelected();
  segmentsStore.segments.forEach(
    segment => Statuses[segment.is_enabled].type === preset && updateMap("set", segment),
  );
};

onMounted(() => {
  sort.value = getUserSetting("segmentsSort");

  const statuses = getUserSetting("segmentsStatusFilter");
  filters.value = {
    statuses:
      statuses && statuses !== "undefined"
        ? Object.fromEntries(statuses.split(",").map(el => [el, Statuses[el].title]))
        : {},
  };

  nextTick(() => {
    const preset = MetricsUtils.All_Periods.find(
      ({ value }) => value !== "since_start_date" && value === getUserSetting("segmentsDateRange"),
    )?.value;

    datePicker.value?.setTimeFromPreset(preset || "last_seven_days");
  });
});
</script>
